import { fromUnixTime, isBefore, startOfDay } from "date-fns";
import { ChevronsUpDown, SquarePlus } from "lucide-react";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { CreateCongressModal } from "@/app/pages/create-congress-modal";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useTranslation } from "react-i18next";

import { useCongresses } from "../api/use-congresses";
import { useCongress } from "../hooks/use-congress";

function CongressSelect() {
  const { t } = useTranslation();
  const {
    data: congresses,
    isLoading: isCongressesLoading,
    isError: isCongressesError,
    isSuccess: isCongressesSuccess,
  } = useCongresses();
  const { congress, setCongress } = useCongress();
  const [searchParams] = useSearchParams();
  const congressIdFromUrl = searchParams.get("congressId");
  const [isCreateCongressModalOpen, setIsCreateCongressModalOpen] =
    useState(false);

  useEffect(() => {
    if (!isCongressesSuccess) {
      return;
    }

    const congressFromUrl = congresses.find(
      (congress) => congress.id === congressIdFromUrl,
    );

    if (!congressFromUrl) {
      return;
    }

    setCongress(congressFromUrl);
  }, [congressIdFromUrl, congresses, isCongressesSuccess, setCongress]);

  if (isCongressesLoading) {
    return (
      <Button
        className="flex w-full flex-1 justify-between shadow-sm"
        variant="outline"
        disabled
      >
        <span>{t("common.selectCongress")}</span>
        <ChevronsUpDown className="size-3.5" />
      </Button>
    );
  }

  if (isCongressesError || !isCongressesSuccess) {
    //TODO: add correct ui
    return t("congressSelect.error");
  }

  const handleChange = (newCongressId: string) => {
    const selectedCongress = congresses.find(
      (congress) => congress.id === newCongressId,
    );

    if (!selectedCongress) {
      throw Error(`Congress with ${newCongressId} id not found`);
    }

    setCongress(selectedCongress);
  };

  const today = startOfDay(new Date());

  const pastCongresses = congresses.filter((congress) =>
    isBefore(startOfDay(fromUnixTime(congress.to)), today),
  );
  const futureCongresses = congresses.filter(
    (congress) =>
      !isBefore(startOfDay(fromUnixTime(congress.to)), today),
  );

  return (
    <>
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <Button
            className="flex w-full flex-1 justify-between shadow-sm"
            variant="outline"
          >
            {congress ? (
              <span className="truncate">{congress.name}</span>
            ) : (
              <span>{t("common.selectCongress")}</span>
            )}
            <ChevronsUpDown className="size-3.5" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          className="max-h-[calc(100vh-10rem)] w-[263px] overflow-y-auto"
          align="end"
        >
          <DropdownMenuItem
            onClick={() => setIsCreateCongressModalOpen(true)}
            className="md:hidden"
          >
            <SquarePlus className="mr-2 size-4" />
            <span>{t("common.newCongress")}</span>
          </DropdownMenuItem>
          <DropdownMenuSeparator className="md:hidden" />

          <DropdownMenuLabel>{t("common.upcoming")}</DropdownMenuLabel>
          {futureCongresses.map((item) => (
            <DropdownMenuCheckboxItem
              key={item.id}
              checked={item.id === congress?.id}
              onCheckedChange={() => handleChange(item.id)}
            >
              {item.name}
            </DropdownMenuCheckboxItem>
          ))}
          <DropdownMenuSeparator />

          <DropdownMenuLabel>{t("common.past")}</DropdownMenuLabel>
          {pastCongresses.map((item) => (
            <DropdownMenuCheckboxItem
              key={item.id}
              checked={item.id === congress?.id}
              onCheckedChange={() => handleChange(item.id)}
            >
              {item.name}
            </DropdownMenuCheckboxItem>
          ))}

          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => setIsCreateCongressModalOpen(true)}>
            <SquarePlus className="mr-2 size-4" />
            <span>{t("common.newCongress")}</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <div id="create-congress-modal">
        <CreateCongressModal
          open={isCreateCongressModalOpen}
          onOpenChange={setIsCreateCongressModalOpen}
        />
      </div>
    </>
  );
}

export { CongressSelect };
